import React, { useEffect, useState, useCallback } from 'react';
import { useJWTAuth } from '../auth/JWTAuthContext';
import { useToast } from '../contexts/ToastContext';
import AuthGuard from '../components/AuthGuard';
import { Transaction as SolanaTransaction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { useCurrentAccount, useSignAndExecuteTransaction, ConnectButton } from '@mysten/dapp-kit';
import { Transaction as SuiTransaction } from '@mysten/sui/transactions';

const Account = () => {
  const { isAuthenticated, getAuthHeaders, user: jwtUser } = useJWTAuth();
  const { success: showSuccess, error: showError, info } = useToast();
  const suiAccount = useCurrentAccount();
  const { mutateAsync: signAndExecuteTransaction } = useSignAndExecuteTransaction();
  const [balances, setBalances] = useState({});
  const [wallets, setWallets] = useState({});
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);
  const [depositing, setDepositing] = useState(null);
  const [withdrawAmountInputs, setWithdrawAmountInputs] = useState({});
  const [depositAmountInputs, setDepositAmountInputs] = useState({});
  const [depositModes, setDepositModes] = useState({});
  const [minWithdrawal, setMinWithdrawal] = useState(100000);
  const [withdrawConfirm, setWithdrawConfirm] = useState({ open: false, chain: '', tokenSymbol: '', amount: 0 });
  const [transactions, setTransactions] = useState([]);
  const [tokenMeta, setTokenMeta] = useState({});
  const [activeTokenTab, setActiveTokenTab] = useState({});
  const [hasWithdrawEntitlement, setHasWithdrawEntitlement] = useState(false);

  const formatAmount = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(val);
  };

  const truncateAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const CopyIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12h6a2 2 0 002-2v-8a2 2 0 00-2-2h-6a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );

  const loadData = useCallback(async (showSpinner = true) => {
    if (!isAuthenticated() || !jwtUser?.id) return;

    if (showSpinner) setLoading(true);
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const [balanceRes, walletsRes, configRes, txRes, tokensRes, entitlementRes] = await Promise.all([
        fetch('/api/ledger/balance', { headers }),
        fetch('/api/ledger/wallets', { headers }),
        fetch('/api/config/minimum_withdrawal_amount', { headers }).catch(() => null),
        fetch(`/api/ledger/user/${jwtUser.id}/transactions`, { headers }),
        fetch('/api/ledger/tokens', { headers }),
        fetch(`/api/entitlements/check/${jwtUser.id}/withdrawal-ban`, { headers }).catch(() => null)
      ]);

      const balanceJson = await balanceRes.json();
      const walletsJson = await walletsRes.json();
      
      setBalances(balanceJson.data || {});
      setWallets(walletsJson.data || {});

      if (configRes && configRes.ok) {
        const configJson = await configRes.json();
        if (configJson.value !== undefined) {
          setMinWithdrawal(Number(configJson.value) || 100000);
        }
      }

      const txJson = await txRes.json();
      console.log('txJson', txJson);
      setTransactions(Array.isArray(txJson.data) ? txJson.data : []);

      const tokensJson = await tokensRes.json();
      const metaMap = {};
      if (tokensJson?.data) {
        for (const token of tokensJson.data) {
          if (token.token_symbol) {
            metaMap[`${token.chain}:${token.token_symbol}`] = token;
          }
          if (token.token_address) {
            metaMap[`${token.chain}:${token.token_address}`] = token;
          }
        }
      }
      setTokenMeta(metaMap);

      // Check withdrawal-ban entitlement (inverted - if they have it, they're banned)
      if (entitlementRes && entitlementRes.ok) {
        const entitlementJson = await entitlementRes.json();
        setHasWithdrawEntitlement(!entitlementJson.hasEntitlement); // Inverted: banned if they have the entitlement
      } else {
        setHasWithdrawEntitlement(true); // Default: allowed (no ban entitlement)
      }
    } catch (e) {
      console.error('Failed loading account data', e);
      showError('Failed to load account data');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, jwtUser?.id, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopy = useCallback(async (value, successLabel = 'Copied to clipboard') => {
    if (!value) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showSuccess(successLabel);
    } catch (copyError) {
      console.error('Clipboard error:', copyError);
      showError('Failed to copy to clipboard');
    }
  }, [showSuccess, showError]);

  const handleWithdraw = async (chain, tokenSymbol, amount) => {
    if (amount < minWithdrawal) {
      showError(`Minimum withdrawal is ${minWithdrawal.toLocaleString()} tokens`);
      return;
    }

    const key = `${chain}:${tokenSymbol}`;
    setWithdrawing(key);
    
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const response = await fetch('/api/ledger/withdraw', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ chain, tokenSymbol, amount })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Handle specific error types
        if (data.error === 'WITHDRAWAL_BANNED' || (response.status === 403 && data.error !== 'MISSING_ENTITLEMENT')) {
          throw new Error(data.message || 'Your account has been restricted from making withdrawals. Please contact support for assistance.');
        }
        if (data.error === 'DAILY_LIMIT_EXCEEDED' || response.status === 429) {
          throw new Error(data.message || 'Daily withdrawal limit has been reached. Please try again tomorrow.');
        }
        if (data.error === 'MISSING_ENTITLEMENT' || (response.status === 403 && data.error === 'MISSING_ENTITLEMENT')) {
          throw new Error(data.message || 'You do not have permission to withdraw tokens. Please contact support to request the withdraw-token entitlement.');
        }
        throw new Error(data.message || data.error || 'Withdrawal failed');
      }

      showSuccess(`Withdrawal successful! Transaction: ${data.signature}`);
      await loadData();
    } catch (error) {
      console.error('Withdrawal error:', error);
      const message = error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : 'Withdrawal failed';
      showError(message);
    } finally {
      setWithdrawing(null);
    }
  };

  const handleDeposit = async (chain, tokenSymbol, amountInput) => {
    const key = `${chain}:${tokenSymbol}`;
    setDepositing(key);

    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const metadataKey = `${chain}:${tokenSymbol}`;
      const meta = tokenMeta[metadataKey] || {};
      const tokenDecimals = typeof meta.decimals === 'number' ? meta.decimals : null;
      const mode = depositModes[key] || 'tokens';
      const { tokens, raw } = tokenDecimals != null ? computeDepositValues(amountInput, tokenDecimals, mode) : { tokens: null, raw: null };
      if (tokenDecimals != null && (tokens == null || raw == null)) {
        throw new Error('Invalid deposit amount');
      }

      // Build deposit transaction
      const response = await fetch('/api/ledger/deposit/build', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chain,
          tokenSymbol,
          amount: mode === 'raw' ? tokens ?? 0 : amountInput,
          rawAmount: tokenDecimals != null ? (mode === 'raw' ? raw : raw) : null
        })
      });

      const buildData = await response.json();

      if (!response.ok || !buildData.success) {
        throw new Error(buildData.message || buildData.error || 'Failed to build deposit transaction');
      }

      const txData = buildData.data;

      const solanaProvider = getSolanaProvider();
      if (!solanaProvider) {
        throw new Error('Phantom wallet not found. Please install Phantom.');
      }

      if (!solanaProvider.isConnected) {
        await solanaProvider.connect();
      }

      if (chain === 'solana') {
        // Solana transaction
        const txBytes = Array.isArray(txData.transaction)
          ? Uint8Array.from(txData.transaction)
          : decodeBase64ToUint8Array(txData.transaction);
        const transaction = SolanaTransaction.from(Buffer.from(txBytes));

        if (!transaction.feePayer) {
          transaction.feePayer = solanaProvider.publicKey || solanaProvider?.publicKey;
        }

        const { signature } = await solanaProvider.signAndSendTransaction(transaction);

        showSuccess(`Deposit transaction sent! Signature: ${signature}`);
        info('Transaction is being processed. Your balance will update once confirmed.');
      } else if (chain === 'sui') {
        // SUI transaction
        if (!suiAccount?.address) {
          throw new Error('Connect a Sui wallet via the Account Linking modal before depositing.');
        }

        const transactionBytes = Array.isArray(txData.transaction)
          ? new Uint8Array(txData.transaction)
          : decodeBase64ToUint8Array(txData.transaction);

        const suiTransaction = SuiTransaction.from(transactionBytes);

        const result = await signAndExecuteTransaction({
          transaction: suiTransaction,
          options: {
            showEffects: true,
            showEvents: true,
            showBalanceChanges: true
          }
        });

        const digest = result?.digest || result?.effects?.transactionDigest || 'pending';
        showSuccess(`Deposit transaction sent! Digest: ${digest}`);
        info('Transaction is being processed. Your balance will update once confirmed.');
      }

      // Reload data after a delay to allow transaction to be processed
      setTimeout(() => {
        loadData();
      }, 3000);
    } catch (error) {
      console.error('Deposit error:', error);
      const message = error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : 'Deposit failed';
      showError(message);
    } finally {
      setDepositing(null);
    }
  };

  const getPrimaryWallet = (chain) => {
    const chainWallets = wallets[chain] || [];
    return chainWallets.find((w) => w.primary) || chainWallets[0] || null;
  };

  const calculateWithdrawAmount = (balance, percentage) => {
    const amt = Math.floor(Number(balance) * (percentage / 100));
    return Number.isFinite(amt) ? amt : 0;
  };

  const decodeBase64ToUint8Array = (base64) => {
    if (typeof base64 !== 'string') return new Uint8Array();
    if (typeof window !== 'undefined' && window.atob) {
      const binary = window.atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    }
    // Fallback using Buffer (Node polyfill)
    return Uint8Array.from(Buffer.from(base64, 'base64'));
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(isoString));
    } catch (_) {
      return isoString;
    }
  };

  const getExplorerUrl = (chain, txId) => {
    if (!txId || txId.includes(':')) return null;
    switch (chain) {
      case 'solana':
        return `https://explorer.solana.com/tx/${txId}?cluster=mainnet`;
      case 'sui':
        return `https://explorer.sui.io/txblock/${txId}?network=mainnet`;
      default:
        return null;
    }
  };

  const computeDepositValues = (inputValue, decimals, mode) => {
    if (!inputValue || !Number.isFinite(decimals)) {
      return { tokens: null, raw: null };
    }

    const numeric = Number(inputValue);
    if (!Number.isFinite(numeric)) {
      return { tokens: null, raw: null };
    }

    if (mode === 'raw') {
      const raw = Math.round(numeric);
      const tokens = raw / Math.pow(10, decimals);
      return { tokens, raw };
    }

    // tokens mode (default)
    const tokens = numeric;
    const raw = Math.round(tokens * Math.pow(10, decimals));
    return { tokens, raw };
  };

  const getSolanaProvider = () => {
    const provider = (window?.phantom && window?.phantom?.solana) || null;
    if (provider?.isPhantom) {
      return provider;
    }
    if (window?.solana?.isPhantom) {
      return window.solana;
    }
    return null;
  };

  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center" style={{
          backgroundImage: `url('/assets/menus/SiteBackground.jpg')`
        }}>
          <div className="text-white text-xl">Loading account...</div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url('/assets/menus/SiteBackground.jpg')`
      }}>
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              <span className="text-yellow-400 text-stroke-black drop-shadow-lg">
                Account
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Manage your ledger balances, withdraw tokens, and make deposits.
            </p>
          </div>

          <div className="space-y-6">
            {Object.keys(balances).length === 0 ? (
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20 text-center">
                <div className="text-gray-300">No ledger balances found.</div>
              </div>
            ) : (
              Object.entries(balances).map(([chain, tokens]) => {
                const primaryWallet = getPrimaryWallet(chain);
                const hasWallet = !!primaryWallet;
                const requiresSuiWallet = chain === 'sui' && !suiAccount?.address;
 
                return (
                  <div key={chain} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-white mb-2">{chain.toUpperCase()}</h2>
                      <div className="text-gray-300 mb-4">
                        {hasWallet ? (
                          <span>Wallet: <span className="font-mono text-sm">{truncateAddress(primaryWallet.address)}</span></span>
                        ) : (
                          <span className="text-yellow-400">No wallet connected</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {Object.keys(tokens || {}).map((tokenKey) => {
                          const tokenId = `${chain}:${tokenKey}`;
                          const label = tokenMeta[tokenId]?.token_symbol || tokenKey;
                          const isActive = activeTokenTab[chain]
                            ? activeTokenTab[chain] === tokenKey
                            : Object.keys(tokens || {})[0] === tokenKey;
                          return (
                            <button
                              key={`${chain}-${tokenKey}`}
                              type="button"
                              onClick={() => setActiveTokenTab({ ...activeTokenTab, [chain]: tokenKey })}
                              className={`px-3 py-1 rounded-full text-sm transition-colors ${isActive
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      {Object.entries(tokens || {}).map(([tokenKey, balanceValue]) => {
                        const metadataKey = `${chain}:${tokenKey}`;
                        const meta = tokenMeta[metadataKey] || {};
                        const canonicalSymbol = meta.token_symbol || tokenKey;
                        const displayLabel = canonicalSymbol;
                        const tokenAddress = meta.token_address;
                        const tokenImage = meta.token_image_url;
                        const balanceNum = Number(balanceValue) || 0;
                        const key = `${chain}:${tokenKey}`;
                        const isActive = activeTokenTab[chain]
                          ? activeTokenTab[chain] === tokenKey
                          : Object.keys(tokens || {})[0] === tokenKey;

                        if (!isActive) {
                          return null;
                        }

                        const isWithdrawing = withdrawing === key;
                        const isDepositing = depositing === key;
                        const withdrawAmount = withdrawAmountInputs[key] || '';
                        const depositAmount = depositAmountInputs[key] || '';
                        const mode = depositModes[key] || 'tokens';
                        const tokenDecimals = typeof meta.decimals === 'number' ? meta.decimals : null;
                        const { tokens: computedTokens, raw: computedRaw } = tokenDecimals != null
                          ? computeDepositValues(depositAmount, tokenDecimals, mode)
                          : { tokens: null, raw: null };
                        const tokenTransactions = Array.isArray(transactions)
                          ? transactions.filter((tx) => tx.chain === chain && (tx.token_symbol === canonicalSymbol || tx.token_symbol === tokenKey))
                          : [];

                        return (
                          <div key={tokenKey} className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                            <div className="flex justify-between items-start mb-4 gap-4">
                              <div className="flex items-center gap-3">
                                {tokenImage ? (
                                  <img
                                    src={tokenImage}
                                    alt={`${displayLabel} icon`}
                                    className="h-12 w-12 rounded-full object-cover border border-gray-700"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="h-12 w-12 rounded-full bg-gray-700 flex items-center justify-center text-lg font-bold text-white">
                                    {displayLabel.slice(0, 2).toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  {tokenAddress && (
                                    <div className="mt-2 inline-flex items-center gap-2 text-xs text-gray-300 bg-gray-900 bg-opacity-60 border border-gray-700 rounded-full px-3 py-1">
                                      <span className="uppercase tracking-wide text-white text-sm">{displayLabel}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleCopy(tokenAddress, 'Token address copied')}
                                        className="text-blue-400 hover:text-blue-200 flex items-center gap-1"
                                      >
                                        <CopyIcon />
                                        <span className="font-mono text-gray-300">{truncateAddress(tokenAddress)}</span>
                                      </button>
                                    </div>
                                  )}
                                  <div className="text-gray-300 text-sm">Balance: {formatAmount(balanceNum)}</div>
                                </div>
                              </div>
                            </div>

                            {!hasWallet && (
                              <div className="mb-4 p-3 bg-yellow-900 bg-opacity-30 border border-yellow-600 rounded text-yellow-200 text-sm">
                                Connect a {chain.toUpperCase()} wallet to withdraw or deposit tokens.
                              </div>
                            )}

                            {chain === 'sui' && hasWallet && requiresSuiWallet && (
                              <div className="mb-4 p-3 bg-blue-900 bg-opacity-30 border border-blue-600 rounded text-blue-100 text-sm flex items-center justify-between">
                                <span>Connect your Sui wallet to sign deposits.</span>
                                <ConnectButton connectText="Connect Sui Wallet" className="ml-3" />
                              </div>
                            )}

                            {hasWallet && (!requiresSuiWallet || chain !== 'sui') && (
                              <>
                                {!hasWithdrawEntitlement && (
                                  <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-600 rounded text-red-200 text-sm">
                                    <strong>Withdrawal Restricted:</strong> Your account has been restricted from making withdrawals. Please contact support for assistance.
                                  </div>
                                )}
                                <div className="mb-4">
                                  <div className="text-sm text-gray-400 mb-2">Withdraw</div>
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {[1, 5, 10, 50].map((pct) => (
                                      <button
                                        key={`${key}-pct-${pct}`}
                                        type="button"
                                        onClick={() => {
                                          const amount = calculateWithdrawAmount(balanceNum, pct);
                                          setWithdrawAmountInputs({ ...withdrawAmountInputs, [key]: String(amount) });
                                        }}
                                        disabled={isWithdrawing || balanceNum < minWithdrawal || !hasWithdrawEntitlement}
                                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded"
                                        title={!hasWithdrawEntitlement ? 'Withdrawals are restricted for your account' : ''}
                                      >
                                        {pct}%
                                      </button>
                                    ))}
                                  </div>
                                  <div className="flex gap-2">
                                    <input
                                      type="number"
                                      value={withdrawAmount}
                                      onChange={(e) => setWithdrawAmountInputs({ ...withdrawAmountInputs, [key]: e.target.value })}
                                      placeholder="Custom amount"
                                      className="flex-1 px-3 py-1 bg-gray-700 text-white rounded text-sm"
                                      min={minWithdrawal}
                                      step="1"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const amt = parseInt(withdrawAmount, 10);
                                        if (Number.isInteger(amt) && amt >= minWithdrawal) {
                                          setWithdrawConfirm({ open: true, chain, tokenSymbol: canonicalSymbol, amount: amt });
                                        } else {
                                          showError(`Withdrawal must be at least ${minWithdrawal.toLocaleString()} tokens`);
                                        }
                                      }}
                                      disabled={isWithdrawing || !withdrawAmount || !hasWithdrawEntitlement}
                                      className="px-4 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded"
                                      title={!hasWithdrawEntitlement ? 'Withdrawals are restricted for your account' : ''}
                                    >
                                      {isWithdrawing ? 'Processing...' : 'Withdraw'}
                                    </button>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    Minimum withdrawal: {minWithdrawal.toLocaleString()} tokens
                                  </div>
                                </div>

                                <div className="mb-4">
                                  <div className="text-sm text-gray-400 mb-2">Deposit</div>
                                  <div className="flex flex-col sm:flex-row gap-2">
                                    <input
                                      type="number"
                                      value={depositAmount}
                                      onChange={(e) => setDepositAmountInputs({ ...depositAmountInputs, [key]: e.target.value })}
                                      placeholder={mode === 'raw' ? 'Raw base units' : 'Token amount'}
                                      className="flex-1 px-3 py-1 bg-gray-700 text-white rounded text-sm"
                                      min="0"
                                      step="any"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const amt = parseFloat(depositAmount);
                                        if (Number.isFinite(amt) && amt > 0) {
                                          handleDeposit(chain, canonicalSymbol, amt);
                                        }
                                      }}
                                      disabled={isDepositing || !depositAmount || parseFloat(depositAmount) <= 0}
                                      className="px-4 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded"
                                    >
                                      {isDepositing ? 'Processing...' : 'Deposit'}
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="mt-6">
                              <div className="text-sm text-gray-400 mb-2">Recent Transactions</div>
                              {tokenTransactions.length > 0 ? (
                                <div className="space-y-3">
                                  {tokenTransactions.map((tx, idx) => {
                                    const amountValue = Number(tx.amount_decimal || tx.amount || 0);
                                    const isWithdrawalTx = amountValue < 0;
                                    let transactionLabel = null;
                                    let transactionLink = null;

                                    if (tx.from_address === 'system:loot' && typeof tx.note === 'string' && tx.note.startsWith('hero_loot:')) {
                                      const parts = tx.note.split(':');
                                      if (parts.length >= 2 && parts[1]) {
                                        transactionLabel = 'Battle Loot';
                                        transactionLink = `/battle/${parts[1]}`;
                                      }
                                    }

                                    if (tx.from_address === 'system:lootfix' ) {
                                      transactionLabel = 'Battle Loot';
                                    }

                                    const explorerUrl = transactionLink ? null : getExplorerUrl(tx.chain, tx.tx_id);
                                    return (
                                      <div
                                        key={`${tx.id || tx.tx_id || idx}-${tokenKey}`}
                                        className="bg-gray-900 bg-opacity-60 border border-gray-700 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-200"
                                      >
                                        <div className="flex items-start sm:items-center gap-3">
                                          <div className={`text-xl ${isWithdrawalTx ? 'text-red-400' : 'text-green-400'}`}>
                                            {isWithdrawalTx ? '⬆️' : '⬇️'}
                                          </div>
                                          <div>
                                            <div className="font-semibold text-white">
                                              {isWithdrawalTx ? 'Withdrawal' : 'Deposit'} {Math.abs(amountValue).toLocaleString()} {displayLabel}
                                            </div>
                                            <div className="text-xs text-gray-400">{formatDateTime(tx.created_at || tx.updated_at)}</div>
                                          </div>
                                        </div>
                                        <div className="mt-2 sm:mt-0 text-xs text-right text-gray-400">
                                          <div className="truncate max-w-[220px] sm:max-w-[300px]">
                                            {transactionLink ? (
                                              <a
                                                href={transactionLink}
                                                className="text-green-400 hover:text-green-300"
                                                target="_new"
                                              >
                                                {transactionLabel || 'Battle Loot'}
                                              </a>
                                            ) : explorerUrl ? (
                                              <a
                                                href={explorerUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-400 hover:text-blue-300"
                                              >
                                                View on Explorer
                                              </a>
                                            ) : (
                                              transactionLabel ? (
                                                <span className="text-gray-500">{transactionLabel}</span>
                                              ) : (
                                                <span className="text-gray-500">Pending signature</span>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">No transactions yet for this token.</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Withdrawal Confirmation Modal */}
      {withdrawConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Confirm Withdrawal</h2>
            <div className="mb-4 text-gray-300">
              <p className="mb-2">
                <strong>Amount:</strong> {withdrawConfirm.amount.toLocaleString()} {withdrawConfirm.tokenSymbol}
              </p>
              <p className="mb-2">
                <strong>Chain:</strong> {withdrawConfirm.chain.toUpperCase()}
              </p>
              <p className="text-sm text-gray-400">
                <strong>To:</strong> {(() => {
                  const primaryWallet = getPrimaryWallet(withdrawConfirm.chain);
                  return primaryWallet ? truncateAddress(primaryWallet.address) : 'No wallet connected';
                })()}
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setWithdrawConfirm({ open: false, chain: '', tokenSymbol: '', amount: 0 })}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  handleWithdraw(withdrawConfirm.chain, withdrawConfirm.tokenSymbol, withdrawConfirm.amount);
                  setWithdrawConfirm({ open: false, chain: '', tokenSymbol: '', amount: 0 });
                }}
                disabled={!!withdrawing}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded"
              >
                {withdrawing ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthGuard>
  );
};

export default Account;
